import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check } from "lucide-react";

const MPSTUNTS_PROMO = {
    id: "mpstunts-10-2026",
    code: "MPSTUNTS",
    discount: 10, // %
    ctaHref: "/services",
};

const BENEFITS = [
    "10% off any treatment or package",
    "Stacks on top of our free consult",
    "Use at online checkout or at the clinic",
    "No minimum. No gimmicks.",
];

// Session-level coordinator: whichever popup opens first sets this,
// preventing a second popup from stacking in the same visit.
export const SESSION_POPUP_FLAG = "zenith_popup_shown_session";

export default function MpstuntsPopup() {
    const STORAGE_KEY = `zenith_promo_seen_${MPSTUNTS_PROMO.id}`;
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const seen = localStorage.getItem(STORAGE_KEY);
        const sessionShown = sessionStorage.getItem(SESSION_POPUP_FLAG);
        if (seen || sessionShown) return;
        const t = setTimeout(() => {
            // double-check in case another popup already claimed this session
            if (!sessionStorage.getItem(SESSION_POPUP_FLAG)) {
                sessionStorage.setItem(SESSION_POPUP_FLAG, MPSTUNTS_PROMO.id);
                setOpen(true);
            }
        }, 2500);
        return () => clearTimeout(t);
    }, [STORAGE_KEY]);

    const close = () => {
        localStorage.setItem(STORAGE_KEY, "1");
        setOpen(false);
    };

    const copy = () => {
        const showCopied = () => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard
                .writeText(MPSTUNTS_PROMO.code)
                .then(showCopied)
                .catch(() => legacyCopy(MPSTUNTS_PROMO.code, showCopied));
        } else {
            legacyCopy(MPSTUNTS_PROMO.code, showCopied);
        }
    };

    const legacyCopy = (text, onSuccess) => {
        try {
            const ta = document.createElement("textarea");
            ta.value = text;
            ta.setAttribute("readonly", "");
            ta.style.position = "absolute";
            ta.style.left = "-9999px";
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            document.body.removeChild(ta);
            onSuccess?.();
        } catch (_) {
            onSuccess?.();
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    data-testid="mpstunts-popup-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
                    onClick={close}
                >
                    <motion.div
                        data-testid="mpstunts-popup-card"
                        initial={{ y: 50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 50, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 220, damping: 26 }}
                        className="relative w-full max-w-md bg-[#F2EDE2] rounded-3xl overflow-hidden shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={close}
                            data-testid="mpstunts-popup-close"
                            aria-label="close"
                            className="absolute top-5 right-5 w-9 h-9 rounded-full bg-white/80 backdrop-blur flex items-center justify-center hover:bg-white transition z-20"
                        >
                            <X size={16} />
                        </button>

                        {/* Header — deep charcoal with gold accents */}
                        <div className="relative bg-[#262422] text-[#F2EDE2] px-8 pt-9 pb-7 overflow-hidden">
                            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#A89070]/15" />
                            <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-[#A89070]/10" />
                            <div className="relative">
                                <div className="text-[10px] tracking-[0.35em] uppercase text-[#A89070] font-medium">
                                    Limited-Time Offer
                                </div>
                                <h3 className="serif text-4xl md:text-[42px] leading-[1.02] mt-3">
                                    Enjoy
                                    <br />
                                    <span className="italic text-[#A89070] font-light">10% off</span>
                                    <br />
                                    <span className="font-medium">your treatment</span>
                                </h3>
                                <p className="mt-3 text-[13px] leading-relaxed text-[#F2EDE2]/85 max-w-[90%]">
                                    A little thank-you from Zenith Wellness.
                                </p>
                            </div>
                        </div>

                        {/* Discount highlight */}
                        <div className="relative -mt-4 mx-8 bg-[#F2EDE2] border border-[#A89070]/40 rounded-2xl px-6 py-5 text-center shadow-[0_10px_25px_-8px_rgba(38,36,34,.25)]">
                            <div className="text-[10px] tracking-[0.32em] uppercase text-[#8C7E6A] font-medium">
                                Apply at Checkout
                            </div>
                            <div
                                className="serif text-6xl leading-none mt-2 text-[#262422]"
                                data-testid="mpstunts-discount"
                            >
                                {MPSTUNTS_PROMO.discount}
                                <span className="text-3xl align-top">%</span>
                            </div>
                            <div className="text-xs mt-1 text-[#544638] tracking-wider">off any service</div>
                        </div>

                        {/* Benefits */}
                        <div className="px-8 pt-6 pb-2">
                            <ul className="space-y-2.5">
                                {BENEFITS.map((b) => (
                                    <li key={b} className="flex items-start gap-3 text-[13px] text-[#262422]">
                                        <span className="mt-[3px] w-4 h-4 rounded-full bg-[#262422] flex items-center justify-center shrink-0">
                                            <Check size={10} strokeWidth={3} className="text-[#A89070]" />
                                        </span>
                                        <span>{b}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Code + CTAs */}
                        <div className="px-8 pt-4 pb-7">
                            <div className="text-center text-[10px] tracking-[0.3em] uppercase text-[#8C7E6A]">
                                Mention at booking or use at checkout
                            </div>
                            <div
                                data-testid="mpstunts-code"
                                className="mt-2 border border-dashed border-[#262422]/50 rounded-full px-6 py-3.5 font-serif text-2xl tracking-[0.28em] text-[#262422] bg-white text-center"
                            >
                                {MPSTUNTS_PROMO.code}
                            </div>

                            <div className="mt-3 grid grid-cols-2 gap-2.5">
                                <button
                                    onClick={copy}
                                    data-testid="mpstunts-copy-btn"
                                    className="rounded-full py-3 text-[11px] uppercase tracking-[0.22em] bg-[#262422] text-[#F2EDE2] hover:bg-black transition"
                                >
                                    {copied ? "Copied" : "Copy Code"}
                                </button>
                                <Link
                                    to={MPSTUNTS_PROMO.ctaHref}
                                    onClick={close}
                                    data-testid="mpstunts-cta-btn"
                                    className="rounded-full py-3 text-[11px] uppercase tracking-[0.22em] border border-[#262422]/60 text-[#262422] hover:bg-[#262422] hover:text-[#F2EDE2] transition text-center"
                                >
                                    Shop Services
                                </Link>
                            </div>

                            <div className="mt-4 text-center text-[10px] leading-relaxed text-[#8C7E6A]">
                                *One per customer. Cannot be combined with other promotions.
                                <br />
                                Discount is applied automatically at Stripe checkout.
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
